import { Check, Dot, ListFilter } from "lucide-react";

import { Popover } from "@/design-system/components/Popover/Popover";
import { Button } from "@/design-system/components/Button/Button";
import { SORT_BY_OPTIONS, SortByOption } from "../constants";

interface EventFilterMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sortBy: SortByOption;
  onSortByChange: (sortBy: SortByOption) => void;
  filterByTag: string | null;
  onFilterByTagChange: (tag: string | null) => void;
  uniqueTags: string[];
  filterByIntegration: string | null;
  onFilterByIntegrationChange: (integration: string | null) => void;
  uniqueIntegrations: string[];
  onReset: () => void;
  hasActiveFilters: boolean;
}

export function EventFilterMenu({
  open,
  onOpenChange,
  sortBy,
  onSortByChange,
  filterByTag,
  onFilterByTagChange,
  uniqueTags,
  filterByIntegration,
  onFilterByIntegrationChange,
  uniqueIntegrations,
  onReset,
  hasActiveFilters,
}: EventFilterMenuProps) {
  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="text-iconDefault hover:bg-surface-hover absolute top-1 right-1 rounded-md p-1 hover:cursor-pointer"
        >
          <ListFilter size={20} />
          {hasActiveFilters && (
            <Dot
              size={30}
              className="text-textActionSelected absolute top-[-10px] right-[-12px]"
            />
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="z-popover border-interactive-selected bg-surface-default flex max-h-110 w-56 flex-col rounded-md border shadow-lg"
          side="bottom"
          align="center"
          sideOffset={4}
        >
          <div className="flex flex-1 flex-col gap-3 overflow-x-hidden overflow-y-auto p-3 pb-0">
            <div>
              <span className="text-textSubtle mb-2 block text-xs font-medium">
                Sort by
              </span>
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => onSortByChange(SORT_BY_OPTIONS.NAME)}
                  className="hover:bg-surface-hover flex items-center justify-between rounded px-2 py-1.5 text-sm hover:cursor-pointer"
                >
                  Name (A-Z)
                  {sortBy === SORT_BY_OPTIONS.NAME && (
                    <Check size={16} className="text-link" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    onSortByChange(SORT_BY_OPTIONS.AD_PLATFORMS_DELIVERED)
                  }
                  className="hover:bg-surface-hover flex items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:cursor-pointer"
                >
                  Supported integrations
                  {sortBy === SORT_BY_OPTIONS.AD_PLATFORMS_DELIVERED && (
                    <Check size={16} className="text-link" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    onSortByChange(SORT_BY_OPTIONS.EVENTS_DELIVERED)
                  }
                  className="hover:bg-surface-hover flex items-center justify-between rounded px-2 py-1.5 text-sm hover:cursor-pointer"
                >
                  Events delivered
                  {sortBy === SORT_BY_OPTIONS.EVENTS_DELIVERED && (
                    <Check size={16} className="text-link" />
                  )}
                </button>
              </div>
            </div>

            <div className="border-borderSubtle border-t pt-3">
              <span className="text-textSubtle mb-2 block text-xs font-medium">
                Filter by integration
              </span>
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => onFilterByIntegrationChange(null)}
                  className="hover:bg-surface-hover flex items-center justify-between rounded px-2 py-1.5 text-sm hover:cursor-pointer"
                >
                  All integrations
                  {filterByIntegration === null && (
                    <Check size={16} className="text-link" />
                  )}
                </button>
                {uniqueIntegrations.map((integration) => (
                  <button
                    key={integration}
                    type="button"
                    onClick={() => onFilterByIntegrationChange(integration)}
                    className="hover:bg-surface-hover flex items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm hover:cursor-pointer"
                  >
                    <span className="min-w-0 break-words">
                      {integration
                        .replace(/_/g, " ")
                        .replace(/^\w/, (c) => c.toUpperCase())}
                    </span>
                    {filterByIntegration === integration && (
                      <Check size={16} className="text-link shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-borderSubtle border-t pt-3">
              <span className="text-textSubtle mb-2 block text-xs font-medium">
                Filter by event definition tag
              </span>
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => onFilterByTagChange(null)}
                  className="hover:bg-surface-hover flex items-center justify-between rounded px-2 py-1.5 text-sm hover:cursor-pointer"
                >
                  All tags
                  {filterByTag === null && (
                    <Check size={16} className="text-link" />
                  )}
                </button>
                {uniqueTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => onFilterByTagChange(tag)}
                    className="hover:bg-surface-hover flex items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm hover:cursor-pointer"
                  >
                    <span className="min-w-0 break-words">{tag}</span>
                    {filterByTag === tag && (
                      <Check size={16} className="text-link shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="border-borderSubtle bg-surface-default sticky bottom-0 rounded-br-lg rounded-bl-lg border-t p-3">
            <Button
              variant="outline"
              onClick={onReset}
              disabled={!hasActiveFilters}
              className="w-full text-sm"
            >
              Reset filters
            </Button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
